import { forwardRef } from "react";
import { Autocomplete, Button, Checkbox, CircularProgress, MenuItem, TextField } from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";

/** Button with a `loading` state (disables and shows a spinner, keeps its width). */
export const AppButton = forwardRef(function AppButton({ loading = false, disabled, startIcon, children, ...props }, ref) {
  return (
    <Button
      ref={ref}
      variant="contained"
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : startIcon}
      {...props}
    >
      {children}
    </Button>
  );
});

/** Small, full-width TextField. All TextField props pass through. */
export const FormField = forwardRef(function FormField(props, ref) {
  return <TextField ref={ref} size="small" fullWidth {...props} />;
});

const toOption = (o) => (typeof o === "object" && o !== null ? o : { value: o, label: String(o) });

/**
 * Select built on TextField. `options` = ["A","B"] or [{ value, label, disabled }].
 * onChange receives the raw event, as with TextField.
 */
export function SelectField({ options = [], placeholder, emptyOption = false, ...props }) {
  return (
    <FormField select {...props}>
      {emptyOption && (
        <MenuItem value="">
          <em>{placeholder || "None"}</em>
        </MenuItem>
      )}
      {options.map(toOption).map((o) => (
        <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled}>
          {o.label}
        </MenuItem>
      ))}
    </FormField>
  );
}

const unchecked = <CheckBoxOutlineBlank fontSize="small" />;
const checked = <CheckBox fontSize="small" />;

/**
 * Autocomplete with a TextField input. `multiple` shows checkboxes and collapses
 * tags after `limitTags`. onChange(value) receives the new value (not the event).
 */
export function AutocompleteField({
  label,
  placeholder,
  options = [],
  value,
  onChange,
  multiple = false,
  loading = false,
  error,
  helperText,
  required,
  limitTags = 2,
  getOptionLabel = (o) => (typeof o === "object" && o !== null ? o.label ?? "" : String(o ?? "")),
  ...props
}) {
  return (
    <Autocomplete
      size="small"
      fullWidth
      multiple={multiple}
      disableCloseOnSelect={multiple}
      limitTags={limitTags}
      options={options}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, v) => onChange?.(v)}
      loading={loading}
      getOptionLabel={getOptionLabel}
      renderOption={
        multiple
          ? (liProps, option, { selected }) => {
              const { key, ...rest } = liProps;
              return (
                <li key={key} {...rest}>
                  <Checkbox icon={unchecked} checkedIcon={checked} checked={selected} sx={{ mr: 0.5 }} />
                  {getOptionLabel(option)}
                </li>
              );
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...props}
    />
  );
}

/**
 * Single-choice autocomplete over [{ value, label }] that reports changes like a
 * native input — onChange({ target: { name, value } }) — so it drops into
 * existing `handleChange(e)` form handlers.
 */
export function OptionSelect({ name, label, value, options = [], onChange, error, helperText, disabled, required, disablePortal = true, sx }) {
  return (
    <Autocomplete
      size="small"
      fullWidth
      disablePortal={disablePortal}
      disableClearable
      options={options}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={options.find((o) => o.value === value) || null}
      onChange={(e, nv) => onChange?.({ target: { name, value: nv?.value || "" } })}
      disabled={disabled}
      sx={sx}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} error={!!error} helperText={error ? helperText || "Required" : helperText} />
      )}
    />
  );
}
